import React, { useState, useEffect } from "react";
import {
  FileText,
  Plus,
  Trash2,
  Eye,
  Edit2,
  FileSignature,
  ClipboardList,
} from "lucide-react";
import { apiClient } from "../../utils/apiClient/index.ts";
import { useAlert } from "../../hooks/useAlert";
import { usePageMeta } from "../../hooks/usePageMeta";
import { PAGE_METADATA } from "../../config/pageMetadata";
import { Table, TableColumn } from "../../components/common/tables/Table";
import { TableContainer } from "../../components/common/tables/TableContainer";
import {
  IconWithTextCell,
  TextCell,
  DateCell,
  ActionsCell,
} from "../../components/common/tables/cells";
import { Modal } from "../../components/common/ui/modal";
import Button from "../../components/common/ui/button/Button";
import { MarkdownRenderer } from "../../components/MarkdownRenderer";
import { useOrg } from "../../contexts/OrgContext.tsx";
import type { RequestType, SignerRole } from "../../types/request";
import { useToast } from "../../contexts/ToastContext.tsx";
import { RequestTemplate, FormTemplateField } from "@secure-caseworker/shared";

type TypeFilter = "all" | "form" | "signature";

export const RequestTemplatesList: React.FC = () => {
  const { showToast } = useToast();
  usePageMeta(PAGE_METADATA.forms);
  const { setAlert } = useAlert();
  const { orgId, navigateOrg } = useOrg();

  const [templates, setTemplates] = useState<RequestTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewTemplate, setPreviewTemplate] =
    useState<RequestTemplate | null>(null);

  useEffect(() => {
    if (orgId) {
      loadTemplates();
    }
  }, [orgId, typeFilter]);

  const loadTemplates = async () => {
    if (!orgId) return;

    setLoading(true);
    try {
      const type = typeFilter === "all" ? undefined : typeFilter;
      const data = await apiClient.getRequestTemplates(orgId, type);
      setTemplates(data as RequestTemplate[]);
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message:
          error instanceof Error ? error.message : "Failed to load templates",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClick = () => {
    navigateOrg(`/request-templates/new`);
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!confirm("Are you sure you want to delete this template?")) return;
    if (!orgId) return;

    try {
      await apiClient.deleteRequestTemplate(templateId, orgId);
      showToast("Template deleted successfully");
      loadTemplates();
    } catch (error) {
      showToast("Failed to delete template", "error");
    }
  };

  const handlePreview = (template: RequestTemplate) => {
    setPreviewTemplate(template);
    setShowPreviewModal(true);
  };

  const handleEdit = (templateId: string) => {
    navigateOrg(`/request-templates/${templateId}/edit`);
  };

  const getTypeIcon = (type: RequestType) => {
    return type === "signature" ? FileSignature : ClipboardList;
  };

  const getTypeBadge = (type: RequestType) => {
    const styles =
      type === "signature"
        ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
        : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400";
    const label = type === "signature" ? "Signature" : "Form";
    return (
      <span className={`text-xs px-2 py-1 rounded-full font-medium ${styles}`}>
        {label}
      </span>
    );
  };

  const getDetailsText = (template: RequestTemplate): string => {
    if (template.type === "form") {
      const fieldCount = template.form_template_fields?.length || 0;
      return `${fieldCount} field${fieldCount !== 1 ? "s" : ""}`;
    } else {
      const signerCount = template.signer_count || 0;
      return `${signerCount} signer${signerCount !== 1 ? "s" : ""}`;
    }
  };

  const getFieldTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      text: "Text",
      email: "Email",
      tel: "Phone",
      textarea: "Textarea",
      select: "Select",
      checkbox: "Checkbox",
      radio: "Radio",
      file: "File Upload",
    };
    return labels[type] || type;
  };

  const columns: TableColumn<RequestTemplate>[] = [
    {
      header: "Type",
      accessor: "type",
      render: (type: RequestType) => getTypeBadge(type),
    },
    {
      header: "Title",
      accessor: "title",
      render: (value: string, row: RequestTemplate) => (
        <IconWithTextCell
          icon={getTypeIcon(row.type)}
          iconColor={row.type === "signature" ? "purple" : "brand"}
          primaryText={value}
          secondaryText={row.description || undefined}
        />
      ),
    },
    {
      header: "Details",
      accessor: "id",
      render: (_: string, row: RequestTemplate) => (
        <TextCell>{getDetailsText(row)}</TextCell>
      ),
    },
    {
      header: "Created",
      accessor: "created_at",
      render: (value: string) => <DateCell date={value} />,
    },
    {
      header: "Actions",
      accessor: "id",
      render: (_: string, row: RequestTemplate) => (
        <ActionsCell
          items={[
            {
              label: "Preview",
              icon: <Eye className="w-4 h-4" />,
              onClick: () => handlePreview(row),
            },
            {
              label: "Edit",
              icon: <Edit2 className="w-4 h-4" />,
              onClick: () => handleEdit(row.id),
            },
            {
              label: "",
              separator: true,
              onClick: () => {},
            },
            {
              label: "Delete",
              icon: <Trash2 className="w-4 h-4" />,
              onClick: () => handleDeleteTemplate(row.id),
              danger: true,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="mb-2 flex items-center gap-3 text-title-md2 font-semibold text-black dark:text-white">
            <FileText size={32} />
            Request Templates
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Create and manage form and signature templates for your organization
          </p>
        </div>
        <Button onClick={handleCreateClick} startIcon={<Plus size={20} />}>
          Create Template
        </Button>
      </div>

      {/* Type Filter Tabs */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
        {(["all", "form", "signature"] as TypeFilter[]).map((filter) => (
          <button
            key={filter}
            onClick={() => setTypeFilter(filter)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              typeFilter === filter
                ? "border-brand-500 text-brand-600 dark:text-brand-400"
                : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            }`}
          >
            {filter === "all"
              ? "All Templates"
              : filter === "form"
                ? "Forms"
                : "Signatures"}
          </button>
        ))}
      </div>

      {/* Templates Table */}
      <TableContainer
        loading={loading}
        loadingText="Loading templates..."
        isEmpty={templates.length === 0}
        emptyIcon={<FileText size={48} />}
        emptyTitle={
          typeFilter === "all"
            ? "No templates yet. Create one to get started."
            : `No ${typeFilter} templates yet.`
        }
        emptyAction={{
          label: "Create Your First Template",
          icon: <Plus size={20} />,
          onClick: handleCreateClick,
        }}
      >
        <Table columns={columns} data={templates} />
      </TableContainer>

      {/* Preview Modal */}
      <Modal
        isOpen={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false);
          setPreviewTemplate(null);
        }}
        title="Template Preview"
        maxWidth="600px"
      >
        {previewTemplate && (
          <div className="space-y-5">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-boxdark/40 dark:bg-gray-800">
              <div className="flex items-center gap-3 mb-2">
                {getTypeBadge(previewTemplate.type)}
                <h3 className="font-semibold text-black dark:text-white">
                  {previewTemplate.title}
                </h3>
              </div>
              {previewTemplate.description && (
                <p className="text-sm text-gray-500 dark:text-gray-500 italic">
                  {previewTemplate.description}
                </p>
              )}
            </div>
            {previewTemplate.instructions && (
              <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                  Participant Instructions:
                </p>
                <div className="dark:text-white">
                  <MarkdownRenderer content={previewTemplate.instructions} />
                </div>
              </div>
            )}

            {previewTemplate.type === "form" &&
              previewTemplate.form_template_fields && (
                <div className="space-y-4">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Form Fields ({previewTemplate.form_template_fields.length}):
                  </p>
                  {previewTemplate.form_template_fields.map(
                    (field: FormTemplateField, index: number) => (
                      <div
                        key={field.id}
                        className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {index + 1}. {field.label}
                              </span>
                              {field.required && (
                                <span className="text-xs text-error-500">
                                  *
                                </span>
                              )}
                            </div>
                          </div>
                          <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                            {getFieldTypeLabel(field.field_type)}
                          </span>
                        </div>
                      </div>
                    ),
                  )}
                </div>
              )}

            {previewTemplate.type === "signature" && (
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Signing Order:{" "}
                    {previewTemplate.routing === "sequential"
                      ? "Sequential"
                      : "Parallel"}
                  </p>
                </div>
                {previewTemplate.signature_template_signers &&
                  previewTemplate.signature_template_signers.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Signer Roles (
                        {previewTemplate.signature_template_signers.length}):
                      </p>
                      {previewTemplate.signature_template_signers.map(
                        (signer: SignerRole, index: number) => (
                          <div
                            key={signer.id}
                            className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800 mb-2"
                          >
                            <div className="flex items-center gap-2">
                              {previewTemplate.routing === "sequential" && (
                                <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 flex items-center justify-center text-xs font-medium">
                                  {index + 1}
                                </span>
                              )}
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {signer.label}
                              </span>
                              {signer.required && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  (Required)
                                </span>
                              )}
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  )}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                variant="outline"
                onClick={() => {
                  setShowPreviewModal(false);
                  setPreviewTemplate(null);
                }}
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
